import React from "react";
import { Helmet } from "react-helmet-async";
import {
  buildConfirmedPersonJsonLdList,
  buildOrganizationJsonLd,
  buildPastEventJsonLdList,
  jsonLdScriptContent,
  type JsonLd,
} from "../utils/siteJsonLd";

type SiteStructuredDataProps = {
  /** Site-wide Organization schema (default true). */
  includeOrganization?: boolean;
  /** When true, also emit past Event + confirmed Person JSON-LD. */
  includePastEvents?: boolean;
};

/**
 * Site-wide Organization schema (+ optional past Events and confirmed speakers).
 * Crawlers primarily see the prerendered copy in `lib/crawl-content.cjs`; this
 * keeps the client head aligned after React boots.
 */
const SiteStructuredData: React.FC<SiteStructuredDataProps> = ({
  includeOrganization = true,
  includePastEvents = false,
}) => {
  const blocks: JsonLd[] = [];
  if (includeOrganization) {
    blocks.push(buildOrganizationJsonLd());
  }
  if (includePastEvents) {
    blocks.push(...buildPastEventJsonLdList());
    blocks.push(...buildConfirmedPersonJsonLdList());
  }

  if (!blocks.length) return null;

  return (
    <Helmet>
      {blocks.map((block, index) => (
        <script key={`ld-${index}-${String(block["@type"] ?? "block")}`} type="application/ld+json">
          {jsonLdScriptContent(block)}
        </script>
      ))}
    </Helmet>
  );
};

export default SiteStructuredData;
